import { Box, Tooltip, Typography } from "@mui/material";
import { Flame } from "lucide-react";
import { LeadTag } from "@/modules/lead-tags/dtos";
import { LeadTagType } from "@/modules/lead-tags/types";
import { muiTheme } from "@/styles/theme";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { useState } from "react";

const indicator = nawyTheme.nawy.colors.semantic.indicator;

const MAX_VISIBLE_TAGS = 1;

const getTagStyle = (tag: LeadTag) => {
  if (tag.type === LeadTagType.HOT_LEAD) {
    return {
      color: indicator.orangeDark,
      borderCoplor: indicator.orange,
      bgColor: indicator.orangeLight,
    };
  }
  return {
    color: muiTheme.colorSchemes.light.palette.primary[500],
    borderColor: muiTheme.colorSchemes.light.palette.primary[500],
    bgColor: "transparent",
  };
};

const TagChip = ({ tag }: { tag: LeadTag }) => {
  const style = getTagStyle(tag);
  const isHot = tag.type === LeadTagType.HOT_LEAD;
  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        color: style.color,
        border: `1px solid ${style.borderColor}`,
        backgroundColor: style.bgColor,
        padding: "2px 6px 2px 8px",
        borderRadius: "12px",
        whiteSpace: "nowrap",
        fontSize: "0.75rem",
        textTransform: "capitalize",
        flexShrink: 0,
        lineHeight: 1.2,
      }}
    >
      {isHot && (
        <Flame
          style={{
            width: "12px",
            height: "12px",
            marginRight: "3px",
            color: style.color,
          }}
        />
      )}
      <Typography
        variant="body_sm_reg"
        sx={{ fontSize: "0.75rem", textTransform: "capitalize" }}
      >
        {tag.name}
      </Typography>
    </Box>
  );
};

const LeadTagsChips = ({ tags }: { tags?: LeadTag[] }) => {
  const [expanded, setExpanded] = useState(false);

  if (!tags || tags.length === 0) return null;

  const visibleTags = expanded ? tags : tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = tags.length - MAX_VISIBLE_TAGS;

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
      {visibleTags.map((tag) => (
        <TagChip key={tag.uid} tag={tag} />
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={expanded ? "Show less" : tags.slice(MAX_VISIBLE_TAGS).map((t) => t.name).join(", ")}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            sx={{
              color: muiTheme.colorSchemes.light.palette.primary[500],
              border: `1px solid ${muiTheme.colorSchemes.light.palette.primary[300]}`,
              backgroundColor: muiTheme.colorSchemes.light.palette.primary[50],
              padding: "2px 8px",
              borderRadius: "12px",
              whiteSpace: "nowrap",
              fontSize: "0.75rem",
              cursor: "pointer",
              lineHeight: 1.2,
              "&:hover": {
                backgroundColor: muiTheme.colorSchemes.light.palette.primary[100],
              },
            }}
          >
            {expanded ? "show less" : `+${hiddenCount} more`}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default LeadTagsChips;
